import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import * as d3 from 'd3'
import { AnimatePresence, motion } from 'framer-motion'
import type { PolicyChainDTO, ProvenanceNode } from '@deepguide-ai/types'
import {
  CheckCircle2,
  FileText,
  FlaskConical,
  Info,
  Maximize2,
  Send,
  Shield,
  X,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { ScrollArea } from '@deepguide-ai/ui'

type VisualNodeType = 'policy' | 'claim' | 'test' | 'submission' | 'evidence'

type GalaxyNode = d3.SimulationNodeDatum & {
  id: string
  label: string
  fullLabel?: string
  type: VisualNodeType
  sourceType: ProvenanceNode['type']
  radius: number
  color: string
  level: number
  metadata?: Record<string, unknown>
  timestamp?: string
  status?: string | null
  assertionSource?: string | null
}

type GalaxyLink = d3.SimulationLinkDatum<GalaxyNode> & {
  id: string
  source: string | GalaxyNode
  target: string | GalaxyNode
  color: string
}

const COLORS: Record<VisualNodeType, string> = {
  policy: '#818cf8',
  claim: '#fbbf24',
  test: '#34d399',
  submission: '#38bdf8',
  evidence: '#fb7185',
}

const RADII = [0, 150, 300, 450, 550]

function getVisualType(type: ProvenanceNode['type']): VisualNodeType {
  if (type === 'pack') return 'evidence'
  if (type === 'policy') return 'policy'
  if (type === 'claim') return 'claim'
  if (type === 'test') return 'test'
  return 'submission'
}

function getNodeLevel(type: ProvenanceNode['type']) {
  switch (type) {
    case 'policy':
      return 0
    case 'claim':
      return 1
    case 'test':
      return 2
    case 'submission':
      return 3
    case 'pack':
      return 4
    default:
      return 4
  }
}

function getNodeRadius(type: ProvenanceNode['type']) {
  switch (type) {
    case 'policy':
      return 15
    case 'claim':
      return 10
    case 'test':
      return 8
    case 'submission':
      return 5
    case 'pack':
      return 4
    default:
      return 4
  }
}

function getLinkColor(relationship: string) {
  switch (relationship) {
    case 'contains':
    case 'verifies':
      return COLORS.claim
    case 'collected_for':
      return COLORS.submission
    case 'packaged_in':
      return COLORS.evidence
    default:
      return 'rgba(255, 255, 255, 0.12)'
  }
}

function formatTimestamp(value?: string | null) {
  if (!value) return null

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString()
}

function getString(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function getNumber(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getStringArray(metadata: Record<string, unknown> | undefined, key: string) {
  const value = metadata?.[key]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && item.length > 0)
}

function getNodeIcon(type: VisualNodeType, className?: string, size = 20) {
  if (type === 'policy') return <Shield className={className} size={size} />
  if (type === 'claim') return <FileText className={className} size={size} />
  if (type === 'test') return <FlaskConical className={className} size={size} />
  if (type === 'submission') return <Send className={className} size={size} />
  return <CheckCircle2 className={className} size={size} />
}

function getClaimDisplayId(nodeId: string) {
  return `CLM-${nodeId.slice(-4).toUpperCase()}`
}

function buildNodeHref(node: GalaxyNode, workspaceId: string) {
  if (node.sourceType === 'test') {
    const testId = getString(node.metadata, 'testId') ?? node.id
    return `/workspaces/${workspaceId}/compliance/tests/${testId}`
  }

  if (node.sourceType === 'submission') {
    const submissionId = getString(node.metadata, 'submissionId') ?? node.id
    return `/workspaces/${workspaceId}/compliance/submissions/${submissionId}`
  }

  if (node.sourceType === 'pack') {
    const evidencePackId = getString(node.metadata, 'evidencePackId') ?? node.id
    return `/workspaces/${workspaceId}/evidence-viewer/${evidencePackId}`
  }

  return null
}

function getDetailRows(node: GalaxyNode) {
  if (node.sourceType === 'policy') {
    return [
      ['Policy slug', getString(node.metadata, 'policySlug')],
      ['Program ID', getString(node.metadata, 'programId')],
    ] as const
  }

  if (node.sourceType === 'claim') {
    return [
      ['Claim ID', getString(node.metadata, 'claimId') ?? node.id],
      ['Control ref', getString(node.metadata, 'controlRef')],
      ['Policy', getString(node.metadata, 'policyName')],
    ] as const
  }

  if (node.sourceType === 'test') {
    const controlRefs = getStringArray(node.metadata, 'controlRefs')
    return [
      ['Test ID', getString(node.metadata, 'testId') ?? node.id],
      ['Task code', getString(node.metadata, 'taskCode')],
      ['Control refs', controlRefs.length > 0 ? controlRefs.join(', ') : null],
    ] as const
  }

  if (node.sourceType === 'submission') {
    const overallScore = getNumber(node.metadata, 'overallScore')
    return [
      ['Submission ID', getString(node.metadata, 'submissionId') ?? node.id],
      ['Status ID', getString(node.metadata, 'statusId')],
      ['Overall score', overallScore != null ? String(overallScore) : null],
    ] as const
  }

  return [
    ['Evidence ID', getString(node.metadata, 'evidencePackId') ?? node.id],
    ['Submission ID', getString(node.metadata, 'submissionId')],
    ['Pack status', getString(node.metadata, 'status')],
  ] as const
}

function getNarrative(node: GalaxyNode, policyTitle: string) {
  if (node.sourceType === 'policy') {
    return `${policyTitle} is the root node for this lineage graph and fans out into every linked claim, test, submission, and evidence pack.`
  }

  if (node.sourceType === 'claim') {
    return 'This claim is attached to the policy and traces downstream into linked tests and their full submission history.'
  }

  if (node.sourceType === 'test') {
    return 'This test aggregates one or more policy claims and includes every recorded submission in the lineage graph.'
  }

  if (node.sourceType === 'submission') {
    return 'This submission captures a specific evidence collection event for the test and links forward into its generated evidence pack.'
  }

  return 'This evidence pack is the terminal artifact in the lineage graph for its submission.'
}

function GalaxyCanvas({
  chain,
  selectedNodeId,
  hoveredNodeId,
  onSelectNode,
  onHoverNode,
}: {
  chain: PolicyChainDTO['graph']
  selectedNodeId: string | null
  hoveredNodeId: string | null
  onSelectNode: (nodeId: string | null) => void
  onHoverNode: (nodeId: string | null) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const [viewport, setViewport] = useState({ width: 0, height: 0 })

  const graph = useMemo(() => {
    const nodes: GalaxyNode[] = chain.nodes
      .filter((node) => node.type !== 'backlink' && node.type !== 'control')
      .map((node) => {
        const visualType = getVisualType(node.type)
        return {
          id: node.id,
          label: node.type === 'claim' ? getClaimDisplayId(node.id) : node.label,
          fullLabel: node.label,
          type: visualType,
          sourceType: node.type,
          radius: getNodeRadius(node.type),
          color: COLORS[visualType],
          level: getNodeLevel(node.type),
          metadata: node.metadata,
          timestamp: node.timestamp,
          status: node.status,
          assertionSource: node.assertionSource ?? null,
        }
      })

    const visibleNodeIds = new Set(nodes.map((node) => node.id))
    const links: GalaxyLink[] = chain.edges
      .filter((edge) => visibleNodeIds.has(edge.from) && visibleNodeIds.has(edge.to))
      .map((edge) => ({
        id: `${edge.from}-${edge.to}-${edge.relationship}`,
        source: edge.from,
        target: edge.to,
        color: getLinkColor(edge.relationship),
      }))

    return { nodes, links }
  }, [chain])

  useEffect(() => {
    if (!containerRef.current) return

    const element = containerRef.current
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return
      setViewport({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!svgRef.current || viewport.width === 0 || viewport.height === 0) return

    const svg = d3.select(svgRef.current)
      .attr('viewBox', `0 0 ${viewport.width} ${viewport.height}`)
      .attr('width', '100%')
      .attr('height', '100%')

    const container = svg.select<SVGGElement>('g.container')
    const nodes = graph.nodes.map((node) => ({ ...node }))
    const links = graph.links.map((link) => ({ ...link }))
    const centerX = viewport.width / 2 + 160
    const centerY = viewport.height / 2

    const simulation = d3.forceSimulation<GalaxyNode>(nodes)
      .force('link', d3.forceLink<GalaxyNode, GalaxyLink>(links).id((node) => node.id).distance(50).strength(0.5))
      .force('charge', d3.forceManyBody().strength(-100))
      .force('center', d3.forceCenter(centerX, centerY))
      .force('radial', d3.forceRadial((node) => RADII[node.level], centerX, centerY).strength(1.5))
      .force('collision', d3.forceCollide().radius((node) => node.radius + 5))

    const linkElements = container.select<SVGGElement>('g.links')
      .selectAll<SVGLineElement, GalaxyLink>('line')
      .data(links, (datum: any) => datum.id)
      .join('line')
      .attr('stroke', (datum) => datum.color)
      .attr('stroke-opacity', 0.1)
      .attr('stroke-width', 1)

    const nodeElements = container.select<SVGGElement>('g.nodes')
      .selectAll<SVGGElement, GalaxyNode>('g.node')
      .data(nodes, (datum: any) => datum.id)
      .join((enter) => {
        const group = enter.append('g')
          .attr('class', 'node')
          .style('cursor', 'pointer')

        group.append('circle')
          .attr('r', (datum) => datum.radius)
          .attr('fill', (datum) => datum.color)
          .attr('filter', 'url(#policy-lineage-glow)')

        group.append('text')
          .attr('dy', (datum) => datum.radius + 12)
          .attr('text-anchor', 'middle')
          .attr('fill', 'rgba(255,255,255,0.52)')
          .style('font-size', '8px')
          .style('font-weight', '600')
          .style('letter-spacing', '0.08em')
          .style('pointer-events', 'none')

        return group
      })
      .on('mouseenter', (_event, datum) => onHoverNode(datum.id))
      .on('mouseleave', () => onHoverNode(null))
      .on('click', (_event, datum) => onSelectNode(datum.id))

    nodeElements.select('circle')
      .attr('r', (datum) => datum.radius)
      .attr('fill', (datum) => datum.color)
      .attr('stroke', (datum) => datum.id === selectedNodeId ? '#ffffff' : 'rgba(255,255,255,0.12)')
      .attr('stroke-width', (datum) => datum.id === selectedNodeId ? 2 : 0.5)

    nodeElements.select('text')
      .text((datum) => datum.level < 3 ? datum.label : '')

    simulation.on('tick', () => {
      linkElements
        .attr('x1', (datum) => (datum.source as GalaxyNode).x!)
        .attr('y1', (datum) => (datum.source as GalaxyNode).y!)
        .attr('x2', (datum) => (datum.target as GalaxyNode).x!)
        .attr('y2', (datum) => (datum.target as GalaxyNode).y!)

      nodeElements.attr('transform', (datum) => `translate(${datum.x},${datum.y})`)
    })

    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 5])
      .on('zoom', (event) => {
        container.attr('transform', event.transform)
      })

    svg.call(zoom)

    return () => {
      simulation.stop()
    }
  }, [graph, onHoverNode, onSelectNode, selectedNodeId, viewport.height, viewport.width])

  useEffect(() => {
    if (!svgRef.current) return

    const svg = d3.select(svgRef.current)

    if (!hoveredNodeId) {
      svg.selectAll<SVGLineElement, GalaxyLink>('line')
        .attr('stroke-opacity', 0.1)
        .attr('stroke-width', 1)
        .attr('stroke', (datum) => datum.color)

      svg.selectAll<SVGGElement, GalaxyNode>('g.node')
        .attr('opacity', 1)

      return
    }

    const connectedNodeIds = new Set<string>([hoveredNodeId])
    const connectedLinkIds = new Set<string>()

    const walkConnections = (id: string) => {
      graph.links.forEach((link) => {
        const sourceId = typeof link.source === 'string' ? link.source : link.source.id
        const targetId = typeof link.target === 'string' ? link.target : link.target.id

        if (sourceId === id) {
          connectedNodeIds.add(targetId)
          connectedLinkIds.add(link.id)
          walkConnections(targetId)
        }

        if (targetId === id) {
          connectedNodeIds.add(sourceId)
          connectedLinkIds.add(link.id)
        }
      })
    }

    walkConnections(hoveredNodeId)

    svg.selectAll<SVGLineElement, GalaxyLink>('line')
      .attr('stroke-opacity', (datum) => connectedLinkIds.has(datum.id) ? 1 : 0.02)
      .attr('stroke-width', (datum) => connectedLinkIds.has(datum.id) ? 2 : 1)
      .attr('stroke', (datum) => connectedLinkIds.has(datum.id) ? '#ffffff' : datum.color)

    svg.selectAll<SVGGElement, GalaxyNode>('g.node')
      .attr('opacity', (datum) => connectedNodeIds.has(datum.id) ? 1 : 0.1)
  }, [graph.links, hoveredNodeId])

  return (
    <div ref={containerRef} className='absolute inset-0 overflow-hidden'>
      <svg ref={svgRef} className='h-full w-full'>
        <defs>
          <filter id='policy-lineage-glow' x='-50%' y='-50%' width='200%' height='200%'>
            <feGaussianBlur stdDeviation='3' result='blur' />
            <feComposite in='SourceGraphic' in2='blur' operator='over' />
          </filter>
        </defs>
        <g className='container'>
          <g className='links' />
          <g className='nodes' />
        </g>
      </svg>
    </div>
  )
}

export function PolicyGalaxyLineageDialog({
  open,
  onOpenChange,
  workspaceId,
  data,
  isLoading,
  error,
  readOnly = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  workspaceId: string
  data?: PolicyChainDTO
  isLoading?: boolean
  error?: string | null
  readOnly?: boolean
}) {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [hoveredNodeId, setHoveredNodeId] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setSelectedNodeId(null)
      setHoveredNodeId(null)
      return
    }

    setSelectedNodeId(data?.graph.rootId ?? null)
  }, [data?.graph.rootId, open])

  const selectedNode = useMemo(
    () => data?.graph.nodes.find((node) => node.id === selectedNodeId) ?? null,
    [data?.graph.nodes, selectedNodeId]
  )

  const selectedGalaxyNode = useMemo<GalaxyNode | null>(() => {
    if (!selectedNode) return null

    const visualType = getVisualType(selectedNode.type)
    return {
      id: selectedNode.id,
      label: selectedNode.type === 'claim' ? getClaimDisplayId(selectedNode.id) : selectedNode.label,
      fullLabel: selectedNode.label,
      type: visualType,
      sourceType: selectedNode.type,
      radius: getNodeRadius(selectedNode.type),
      color: COLORS[visualType],
      level: getNodeLevel(selectedNode.type),
      metadata: selectedNode.metadata,
      timestamp: selectedNode.timestamp,
      status: selectedNode.status,
      assertionSource: selectedNode.assertionSource ?? null,
    }
  }, [selectedNode])

  const counts = data?.summary ?? {
    claims: 0,
    tests: 0,
    submissions: 0,
    evidence: 0,
  }

  const totalEntities = data?.graph.nodes.filter((node) => node.type !== 'backlink' && node.type !== 'control').length ?? 0
  const totalLinks = data?.graph.edges.filter((edge) => edge.relationship !== 'references' && edge.relationship !== 'maps_to').length ?? 0
  const policyTitle = data?.policy.title ?? 'Policy'
  const href = selectedGalaxyNode && !readOnly ? buildNodeHref(selectedGalaxyNode, workspaceId) : null
  const detailRows = selectedGalaxyNode ? getDetailRows(selectedGalaxyNode) : []
  const timestamp = formatTimestamp(selectedGalaxyNode?.timestamp)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='z-[2147483647] h-[calc(100vh-48px)] !max-w-[calc(100vw-48px)] overflow-hidden border-white/10 bg-[#020617] p-0 text-white shadow-2xl'>
        <DialogTitle className='sr-only'>Policy lineage</DialogTitle>
        <DialogDescription className='sr-only'>
          Orbit-style policy lineage graph for claims, tests, submissions, and evidence.
        </DialogDescription>
        <div className='relative h-full w-full overflow-hidden bg-[#020617]'>
          <div className='pointer-events-none absolute inset-0'>
            <div className='absolute top-1/2 left-1/2 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-600/10 blur-[120px]' />
            <div className='absolute -top-40 -left-40 h-[600px] w-[600px] rounded-full bg-emerald-600/5 blur-[100px]' />
            <div className='absolute -right-40 -bottom-40 h-[600px] w-[600px] rounded-full bg-rose-600/5 blur-[100px]' />
          </div>

          {data?.graph ? (
            <GalaxyCanvas
              chain={data.graph}
              selectedNodeId={selectedNodeId}
              hoveredNodeId={hoveredNodeId}
              onSelectNode={setSelectedNodeId}
              onHoverNode={setHoveredNodeId}
            />
          ) : null}

          <div className='pointer-events-none absolute top-8 left-8'>
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className='pointer-events-auto rounded-md border border-white/10 bg-slate-900/40 p-6 shadow-2xl backdrop-blur-xl'
            >
              <div className='flex items-center gap-4'>
                <div className='flex h-12 w-12 items-center justify-center rounded-md border border-indigo-500/30 bg-indigo-500/20'>
                  <Shield className='text-indigo-400' size={24} />
                </div>
                <div>
                  <h2 className='text-2xl font-black leading-none tracking-tight uppercase text-white'>
                    Policy Galaxy
                  </h2>
                  <p className='mt-1 text-[10px] font-mono tracking-[0.18em] uppercase text-indigo-400/60'>
                    Orbital Lineage Engine v3.0
                  </p>
                  <p className='mt-2 max-w-[280px] text-xs text-slate-300'>
                    {policyTitle}
                  </p>
                </div>
              </div>

              <div className='mt-8 space-y-4'>
                <LegendItem color={COLORS.policy} label='Policy core' count={1} icon={getNodeIcon('policy', undefined, 12)} />
                <LegendItem color={COLORS.claim} label='Claims orbit' count={counts.claims} icon={getNodeIcon('claim', undefined, 12)} />
                <LegendItem color={COLORS.test} label='Test nodes' count={counts.tests} icon={getNodeIcon('test', undefined, 12)} />
                <LegendItem color={COLORS.submission} label='Submissions' count={counts.submissions} icon={getNodeIcon('submission', undefined, 12)} />
                <LegendItem color={COLORS.evidence} label='Evidence edge' count={counts.evidence} icon={getNodeIcon('evidence', undefined, 12)} />
              </div>

              <div className='mt-8 flex flex-col gap-2 border-t border-white/5 pt-6'>
                <div className='flex items-center gap-2 text-[10px] text-slate-400'>
                  <Maximize2 size={12} />
                  Scroll to zoom, drag to explore
                </div>
                <div className='flex items-center gap-2 text-[10px] text-slate-400'>
                  <Info size={12} />
                  Hover to trace gravity path
                </div>
              </div>
            </motion.div>
          </div>

          <div className='pointer-events-none absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-8 rounded-full border border-white/10 bg-slate-900/40 px-8 py-3 shadow-2xl backdrop-blur-2xl'>
            <div className='flex items-center gap-2'>
              <span className='h-1.5 w-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.8)] animate-pulse' />
              <span className='text-[10px] font-black tracking-widest uppercase text-white'>Policy Engine Active</span>
            </div>
            <div className='h-4 w-px bg-white/10' />
            <div className='flex items-center gap-4 text-[9px] font-mono tracking-tight uppercase text-slate-500'>
              <span>{totalEntities} Entities</span>
              <span>{totalLinks} Gravity Links</span>
              <span>All Submissions</span>
            </div>
          </div>

          <AnimatePresence>
            {selectedGalaxyNode && (
              <motion.div
                initial={{ x: 400, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: 400, opacity: 0 }}
                className='absolute left-8 bottom-8 dark z-[2147483647] w-80 flex-col h-[calc(100%-480px)] overflow-y-hidden flex rounded-md border border-white/10 bg-slate-900/80 py-6 shadow-2xl backdrop-blur-2xl'
              >
                <div className='mb-6 px-6 flex flex-shirk-0 items-center gap-4 justify-start'>
                  <div className='rounded-md border border-white/10 bg-white/5 p-2'>
                    <span style={{ color: selectedGalaxyNode.color }}>
                      {getNodeIcon(selectedGalaxyNode.type, undefined, 20)}
                    </span>
                  </div>
                  <h4 className='mb-1 text-xl font-bold text-white'>
                    {selectedGalaxyNode.sourceType === 'claim'
                      ? getClaimDisplayId(selectedGalaxyNode.id)
                      : selectedGalaxyNode.label}
                  </h4>
                  {/* <button
                    type='button'
                    onClick={() => setSelectedNodeId(null)}
                    className='rounded-full p-1 transition-colors hover:bg-white/10'
                  >
                    <X size={20} className='text-slate-400' />
                  </button> */}
                </div>

             <ScrollArea className='flex-1 px-6 min-h-0'>
                <p className='mb-6 text-xs font-mono tracking-widest uppercase text-slate-400'>
                  {selectedGalaxyNode.type} metadata
                </p>

                <div className='space-y-4'>
                  {selectedGalaxyNode.sourceType === 'claim' ? (
                    <div className='rounded-md border border-white/5 bg-white/5 p-4 text-xs leading-relaxed text-slate-300'>
                      {selectedNode?.label}
                    </div>
                  ) : null}
                  <DetailRow label='Node ID' value={selectedGalaxyNode.id} />
                  <DetailRow label='Orbit level' value={String(selectedGalaxyNode.level)} />
                  <DetailRow label='Status' value={selectedGalaxyNode.status ?? 'Observed'} accent={selectedGalaxyNode.status ? 'success' : 'default'} />
                  {timestamp ? <DetailRow label='Timestamp' value={timestamp} /> : null}
                  {detailRows.map(([label, value]) => (
                    value ? <DetailRow key={label} label={label} value={value} /> : null
                  ))}
                  <div className='rounded-md border border-white/5 bg-white/5 p-4 text-xs leading-relaxed text-slate-300'>
                    {getNarrative(selectedGalaxyNode, policyTitle)}
                  </div>
                </div>

                {href ? (
                  <a
                    href={href}
                    className='mt-8 inline-flex w-full items-center justify-center rounded-md bg-indigo-600 py-3 text-sm font-bold text-white shadow-lg shadow-indigo-600/20 transition-all hover:bg-indigo-500'
                  >
                    View Detailed Report
                  </a>
                ) : null}
                </ScrollArea>
              </motion.div>
            )}
          </AnimatePresence>

          {(isLoading || error || !data) && (
            <div className='absolute inset-0 flex items-center justify-center bg-[#020617]/70 backdrop-blur-sm'>
              <div className='rounded-md border border-white/10 bg-slate-900/70 px-6 py-4 text-sm text-slate-200'>
                {isLoading ? 'Loading policy lineage…' : error ?? 'No lineage available'}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function LegendItem({
  color,
  label,
  count,
  icon,
}: {
  color: string
  label: string
  count: number
  icon: ReactNode
}) {
  return (
    <div className='group flex items-center justify-between'>
      <div className='flex items-center gap-3'>
        <div className='h-2 w-2 rounded-full' style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }} />
        <span className='flex items-center gap-1.5 text-[11px] font-bold tracking-wider uppercase text-slate-300 transition-colors group-hover:text-white'>
          {icon}
          {label}
        </span>
      </div>
      <span className='text-[10px] font-mono text-slate-500'>{count}</span>
    </div>
  )
}

function DetailRow({
  label,
  value,
  accent = 'default',
}: {
  label: string
  value: string
  accent?: 'default' | 'success'
}) {
  return (
    <div className='flex items-center justify-between border-b border-white/5 py-2'>
      <span className='text-[10px] font-mono uppercase text-slate-500'>{label}</span>
      <span className={accent === 'success' ? 'text-xs font-bold text-emerald-400' : 'text-right text-xs font-bold text-slate-200'}>
        {value}
      </span>
    </div>
  )
}
